import React, { useMemo } from 'react';
import { Meta, Display } from '../../components/Atoms';
import { LoadingState, ErrorState } from '../../components/states';
import { useMultipleApi, useApi, daysSince } from '../../lib/api';
import ProfileShell, { ProfileSection } from './ProfileShell';

/**
 * ProfileStats - statystyki sezonu (plony, zadania, retencja).
 *
 * Endpointy:
 *   /api/plots/all-with-details - dla harvest stats
 *   /api/tasks?limit=500 - dla task stats
 *   /api/auth/me - dla daty rejestracji
 */

function StatBlock({ label, value, unit, accent }) {
  return (
    <div style={{
      padding: '20px 22px', borderRadius: 14,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
    }}>
      <Meta style={{ marginBottom: 8 }}>{label}</Meta>
      <Display size={32} style={{
        display: 'block', letterSpacing: '-.02em',
        color: accent || 'var(--t-text)',
      }}>{value}</Display>
      {unit && (
        <div style={{
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.14em', textTransform: 'uppercase',
          color: 'var(--t-text-mute)', marginTop: 4,
        }}>{unit}</div>
      )}
    </div>
  );
}

function HarvestRow({ bed, plotName }) {
  return (
    <div style={{
      display: 'flex', gap: 14, alignItems: 'center',
      padding: '12px 0',
      borderBottom: '1px solid var(--t-line)',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, color: 'var(--t-text)',
          letterSpacing: '-.01em',
        }}>{bed.plant_name}{bed.plant_variety && ` (${bed.plant_variety})`}</div>
        <div style={{ fontSize: 11, color: 'var(--t-text-dim)', marginTop: 2 }}>
          {plotName} · {new Date(bed.actual_harvest_date).toLocaleDateString('pl-PL')}
        </div>
      </div>
      <Display size={16} style={{ color: 'var(--t-amber)' }}>
        {bed.yield_amount} {bed.yield_unit || 'szt'}
      </Display>
    </div>
  );
}

export default function ProfileStats() {
  const { results, loading, errors } = useMultipleApi({
    me: '/api/auth/me',
    plots: '/api/plots/all-with-details',
    tasks: '/api/tasks?limit=500',
  });
  const { data: streaks } = useApi('/api/profile/streaks');

  // Hooki muszą być przed early return — reguły React Hooks
  const stats = useMemo(() => {
    const user = results.me?.user || {};
    const allPlots = results.plots || [];
    const allBeds = allPlots.flatMap(p => (p.beds || []).map(b => ({ ...b, plot_name: p.name })));
    const allTasks = results.tasks?.data || [];

    const completed = allTasks.filter(t => t.completed);
    const upcoming = allTasks.filter(t => !t.completed && t.due_date && new Date(t.due_date) >= new Date());
    const overdue = allTasks.filter(t => !t.completed && t.due_date && new Date(t.due_date) < new Date());

    const harvested = allBeds.filter(b => b.actual_harvest_date && b.yield_amount);
    const totalYieldKg = harvested
      .filter(b => b.yield_unit?.toLowerCase().includes('kg'))
      .reduce((sum, b) => sum + (parseFloat(b.yield_amount) || 0), 0);
    const totalHarvests = harvested.length;

    const daysActive = user.created_at ? Math.max(1, -daysSince(user.created_at)) : 0;
    const avgTasksPerWeek = daysActive > 0
      ? ((completed.length / daysActive) * 7).toFixed(1)
      : '0';

    return {
      completed: completed.length,
      upcoming: upcoming.length,
      overdue: overdue.length,
      harvested,
      totalHarvests,
      totalYieldKg,
      daysActive: Math.abs(daysActive),
      avgTasksPerWeek,
      plotCount: allPlots.length,
      bedCount: allBeds.length,
    };
  }, [results]);

  const error = errors.me || errors.plots;
  if (error && (error.includes('autoryz') || error === 'Brak tokenu autoryzacji')) {
    return <ProfileShell><ErrorState error={{ status: 401, message: error }} /></ProfileShell>;
  }
  if (loading) return <ProfileShell><LoadingState /></ProfileShell>;

  return (
    <ProfileShell
      eyebrow="Statystyki"
      title="Twój sezon w liczbach"
      subtitle="Wszystko, co aplikacja zapamiętała o Twoim ogrodzie."
    >
      {/* Streaks section */}
      {streaks && streaks.length > 0 && (
        <ProfileSection title="Serie (streaki)">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
          }}>
            {streaks.map(s => (
              <div key={s.type} style={{
                padding: '18px 20px', borderRadius: 14,
                background: 'var(--t-bg-elev)',
                border: '1px solid var(--t-line)',
                textAlign: 'center',
              }}>
                <Meta style={{ marginBottom: 8 }}>
                  {s.type === 'task_complete' ? 'Zadania' :
                   s.type === 'photo_add' ? 'Zdjęcia' : s.type}
                </Meta>
                <Display size={32} style={{
                  display: 'block', letterSpacing: '-.025em',
                  color: 'var(--t-amber)',
                }}>{s.current}</Display>
                <div style={{
                  fontFamily: 'var(--t-mono)', fontSize: 10,
                  letterSpacing: '.12em', textTransform: 'uppercase',
                  color: 'var(--t-text-mute)', marginTop: 4,
                }}>dni z rzędu</div>
                <div style={{
                  marginTop: 6, fontSize: 11,
                  fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
                  color: 'var(--t-text-dim)',
                }}>rekord: {s.longest}</div>
              </div>
            ))}
          </div>
        </ProfileSection>
      )}

      <ProfileSection title="Sezon ogólny">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}>
          <StatBlock label="Działki" value={stats.plotCount} />
          <StatBlock label="Grządki" value={stats.bedCount} />
          <StatBlock label="Dni z aplikacją" value={stats.daysActive} />
          <StatBlock label="Zad./tydzień" value={stats.avgTasksPerWeek} />
        </div>
      </ProfileSection>

      <ProfileSection title="Zadania">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}>
          <StatBlock label="Wykonane" value={stats.completed} accent="var(--t-sage)" />
          <StatBlock label="Najbliższe" value={stats.upcoming} />
          <StatBlock label="Spóźnione" value={stats.overdue} accent={stats.overdue > 0 ? 'var(--t-rust)' : undefined} />
        </div>
      </ProfileSection>

      <ProfileSection title="Plony">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12, marginBottom: 24,
        }}>
          <StatBlock label="Zbiorów" value={stats.totalHarvests} />
          <StatBlock label="Łącznie" value={stats.totalYieldKg.toFixed(1)} unit="kg" accent="var(--t-amber)" />
        </div>

        {stats.harvested.length > 0 ? (
          <>
            <Meta style={{ marginBottom: 12 }}>Ostatnie zbiory</Meta>
            <div>
              {stats.harvested
                .sort((a, b) => new Date(b.actual_harvest_date) - new Date(a.actual_harvest_date))
                .slice(0, 10)
                .map(b => (
                  <HarvestRow key={b.id} bed={b} plotName={b.plot_name} />
                ))}
            </div>
          </>
        ) : (
          <div style={{
            padding: '20px 0', textAlign: 'center',
            fontSize: 13, color: 'var(--t-text-mute)',
            fontStyle: 'italic',
          }}>↳ Brak zarejestrowanych zbiorów. Dodaj pierwszy z grządki.</div>
        )}
      </ProfileSection>

      <div style={{
        marginTop: 24, fontSize: 11, color: 'var(--t-text-mute)',
        fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
        textAlign: 'center',
      }}>
        Aplikacja jest darmowa — chcesz wesprzeć projekt? Powiedz znajomym.
      </div>
    </ProfileShell>
  );
}
